import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  ChartBarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PlusIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import cohortService from '../../services/cohortService';

interface Cohort {
  id: string;
  name: string;
  description: string;
  creatorId: string;
  criteria: {
    joinDateStart?: string;
    joinDateEnd?: string;
    tierIds?: string[];
    minPledgeAmount?: number;
    maxPledgeAmount?: number;
  };
  patronCount: number;
  activePatrons: number;
  retentionRate: number;
  averageLifetimeValue: number;
  totalRevenue: number;
  createdAt: string;
  lastCalculatedAt?: string;
}

interface RetentionData {
  cohortId: string;
  cohortName: string;
  month: number;
  patronCount: number;
  retentionRate: number;
  revenue: number;
}

interface CohortComparison {
  cohortId: string;
  cohortName: string;
  metrics: {
    patronCount: number;
    retentionRate: number;
    averageLifetimeValue: number;
    totalRevenue: number;
    churnRate: number;
  };
}

const AdminCohortAnalyticsPage: React.FC = () => {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [selectedCohort, setSelectedCohort] = useState<Cohort | null>(null);
  const [retentionData, setRetentionData] = useState<RetentionData[]>([]);
  const [comparisonData, setComparisonData] = useState<CohortComparison[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCreatorId] = useState<string>('creator-1'); // Default creator
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCohorts, setFilteredCohorts] = useState<Cohort[]>([]);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  
  // Forms
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    joinDateStart: '',
    joinDateEnd: '',
    minPledgeAmount: '',
    maxPledgeAmount: '',
  });

  useEffect(() => {
    if (selectedCreatorId) {
      loadCohorts();
    }
  }, [selectedCreatorId]);

  useEffect(() => {
    filterCohorts();
  }, [cohorts, searchQuery]);

  useEffect(() => {
    if (selectedCohort) {
      loadRetentionData();
    }
  }, [selectedCohort]);

  const loadCohorts = async () => {
    try {
      setLoading(true);
      const patronCohorts = await cohortService.getCohorts(selectedCreatorId);
      
      // Map PatronCohort to our Cohort interface
      const mappedCohorts: Cohort[] = patronCohorts.map((pc: any) => ({
        id: pc.id,
        name: `${pc.cohortType} Cohort`,
        description: `Started: ${new Date(pc.startDate).toLocaleDateString()}`,
        creatorId: pc.creatorProfileId,
        criteria: {
          joinDateStart: pc.startDate,
          joinDateEnd: pc.endDate,
        },
        patronCount: pc.patronCount,
        activePatrons: Math.round(pc.patronCount * (pc.retention30Day / 100)),
        retentionRate: pc.retention30Day,
        averageLifetimeValue: pc.averageLifetimeValue,
        totalRevenue: pc.averageLifetimeValue * pc.patronCount,
        createdAt: pc.createdAt,
        lastCalculatedAt: pc.lastCalculatedAt,
      }));
      
      setCohorts(mappedCohorts);
      if (mappedCohorts.length > 0 && !selectedCohort) {
        setSelectedCohort(mappedCohorts[0]);
      }
    } catch (error) {
      console.error('Error loading cohorts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRetentionData = async () => {
    if (!selectedCohort) return;
    
    try {
      const cohortRetention = await cohortService.getCohortRetention(selectedCohort.id);
      
      // Map CohortRetentionData (month0-month12) to RetentionData[]
      const mappedRetention: RetentionData[] = [];
      for (let i = 0; i <= 12; i++) {
        const monthKey = `month${i}` as keyof typeof cohortRetention;
        const rate = cohortRetention[monthKey] || 0;
        mappedRetention.push({
          cohortId: selectedCohort.id,
          cohortName: selectedCohort.name,
          month: i,
          patronCount: Math.round(selectedCohort.patronCount * (rate / 100)),
          retentionRate: rate,
          revenue: selectedCohort.averageLifetimeValue * Math.round(selectedCohort.patronCount * (rate / 100)) / 12,
        });
      }
      
      setRetentionData(mappedRetention.filter(d => d.retentionRate > 0));
    } catch (error) {
      console.error('Error loading retention data:', error);
    }
  };

  const filterCohorts = () => {
    let filtered = [...cohorts];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cohort =>
        cohort.name.toLowerCase().includes(query) ||
        cohort.description.toLowerCase().includes(query)
      );
    }
    
    setFilteredCohorts(filtered);
  };

  const handleCreateCohort = async () => {
    try {
      const startDate = createForm.joinDateStart ? new Date(createForm.joinDateStart) : new Date();
      const endDate = createForm.joinDateEnd ? new Date(createForm.joinDateEnd) : undefined;
      
      await cohortService.createCohort({
        creatorProfileId: selectedCreatorId,
        cohortType: 'CUSTOM',
        startDate,
        endDate,
        metadata: {
          minPledgeAmount: createForm.minPledgeAmount ? Number(createForm.minPledgeAmount) : undefined,
          maxPledgeAmount: createForm.maxPledgeAmount ? Number(createForm.maxPledgeAmount) : undefined,
          name: createForm.name,
          description: createForm.description,
        },
      });
      
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        description: '',
        joinDateStart: '',
        joinDateEnd: '',
        minPledgeAmount: '',
        maxPledgeAmount: '',
      });
      loadCohorts();
    } catch (error) {
      console.error('Error creating cohort:', error);
      alert('Failed to create cohort');
    }
  };

  const handleRecalculate = async (cohortId: string) => {
    try {
      await cohortService.recalculateCohort(cohortId);
      loadCohorts();
      if (selectedCohort?.id === cohortId) {
        loadRetentionData();
      }
    } catch (error) {
      console.error('Error recalculating cohort:', error);
      alert('Failed to recalculate cohort');
    }
  };

  const handleCompare = async () => {
    if (compareIds.length < 2) {
      alert('Please select at least 2 cohorts to compare');
      return;
    }
    
    try {
      const comparison = await cohortService.compareCohorts(compareIds);
      
      // Map CohortComparison to CohortComparison[]
      const mappedComparison: CohortComparison[] = comparison.cohorts.map((cohort: any) => ({
        cohortId: cohort.id,
        cohortName: `${cohort.cohortType} Cohort`,
        metrics: {
          patronCount: cohort.patronCount,
          retentionRate: cohort.retention30Day,
          averageLifetimeValue: cohort.averageLifetimeValue,
          totalRevenue: cohort.patronCount * cohort.averageLifetimeValue,
          churnRate: cohort.churnRate,
        },
      }));
      
      setComparisonData(mappedComparison);
      setShowComparisonModal(true);
    } catch (error) {
      console.error('Error comparing cohorts:', error);
      alert('Failed to compare cohorts');
    }
  };

  const handleExport = async (cohortId: string) => {
    try {
      const blob = await cohortService.exportCohort(cohortId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cohort-${cohortId}-export.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting cohort:', error);
      alert('Failed to export cohort');
    }
  };

  const toggleCompareSelection = (cohortId: string) => {
    setCompareIds(prev =>
      prev.includes(cohortId)
        ? prev.filter(id => id !== cohortId)
        : [...prev, cohortId]
    );
  };

  if (!selectedCreatorId) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No Creator Selected</h3>
          <p className="mt-1 text-sm text-gray-500">
            Please select a creator to view cohort analytics.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-gray-600">Loading cohort analytics...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Cohort Analytics Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">
          Analyze patron cohorts, retention rates, and lifetime value over time.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Sidebar - Cohort List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Cohorts</h2>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  New
                </button>
              </div>
              
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Search cohorts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {filteredCohorts.length === 0 ? (
                <div className="p-6 text-center">
                  <UserGroupIcon className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No cohorts found</p>
                </div>
              ) : (
                filteredCohorts.map((cohort) => (
                  <div
                    key={cohort.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      selectedCohort?.id === cohort.id ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''
                    }`}
                    onClick={() => setSelectedCohort(cohort)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900">{cohort.name}</h3>
                        <p className="text-xs text-gray-500 mt-1">{cohort.description}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={compareIds.includes(cohort.id)}
                        onChange={() => toggleCompareSelection(cohort.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">Patrons:</span>
                        <span className="ml-1 font-medium text-gray-900">{cohort.patronCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Active:</span>
                        <span className="ml-1 font-medium text-gray-900">{cohort.activePatrons}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Retention:</span>
                        <span className="ml-1 font-medium text-green-600">{cohort.retentionRate.toFixed(1)}%</span>
                      </div>
                      <div>
                        <span className="text-gray-500">LTV:</span>
                        <span className="ml-1 font-medium text-gray-900">${cohort.averageLifetimeValue.toFixed(0)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {compareIds.length > 0 && (
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={handleCompare}
                  disabled={compareIds.length < 2}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChartBarIcon className="h-5 w-5 mr-2" />
                  Compare {compareIds.length} Cohorts
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content - Analytics */}
        <div className="lg:col-span-2">
          {selectedCohort ? (
            <div className="space-y-6">
              {/* Cohort Overview Card */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{selectedCohort.name}</h2>
                    <p className="text-sm text-gray-500">{selectedCohort.description}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleRecalculate(selectedCohort.id)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <ArrowPathIcon className="h-4 w-4 mr-1" />
                      Recalculate
                    </button>
                    <button
                      onClick={() => handleExport(selectedCohort.id)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                      Export
                    </button>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <UserGroupIcon className="h-8 w-8 text-blue-500" />
                      <div className="ml-3">
                        <p className="text-xs text-gray-500">Total Patrons</p>
                        <p className="text-xl font-semibold text-gray-900">{selectedCohort.patronCount}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <UserGroupIcon className="h-8 w-8 text-green-500" />
                      <div className="ml-3">
                        <p className="text-xs text-gray-500">Active Patrons</p>
                        <p className="text-xl font-semibold text-gray-900">{selectedCohort.activePatrons}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <ArrowTrendingUpIcon className="h-8 w-8 text-purple-500" />
                      <div className="ml-3">
                        <p className="text-xs text-gray-500">Retention Rate</p>
                        <p className="text-xl font-semibold text-green-600">{selectedCohort.retentionRate.toFixed(1)}%</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-8 w-8 text-yellow-500" />
                      <div className="ml-3">
                        <p className="text-xs text-gray-500">Avg LTV</p>
                        <p className="text-xl font-semibold text-gray-900">${selectedCohort.averageLifetimeValue.toFixed(0)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-xs text-gray-500">
                  Last calculated: {selectedCohort.lastCalculatedAt ? new Date(selectedCohort.lastCalculatedAt).toLocaleDateString() : 'Never'}
                </div>
              </div>

              {/* Retention Chart (Text-based for now) */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Retention Over Time</h3>
                
                {retentionData.length === 0 ? (
                  <div className="text-center py-12">
                    <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">No retention data available</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {retentionData.map((data, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-20 text-sm text-gray-600">Month {data.month}</div>
                        <div className="flex-1">
                          <div className="flex items-center">
                            <div className="flex-1 bg-gray-200 rounded-full h-8 overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-indigo-500 to-purple-600 h-full flex items-center justify-end pr-2 text-xs font-medium text-white transition-all duration-500"
                                style={{ width: `${data.retentionRate}%` }}
                              >
                                {data.retentionRate.toFixed(1)}%
                              </div>
                            </div>
                            <div className="ml-4 w-24 text-right">
                              <div className="text-sm font-medium text-gray-900">{data.patronCount} patrons</div>
                              <div className="text-xs text-gray-500">${data.revenue.toFixed(0)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Revenue Trend */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-green-600 font-medium">Total Revenue</p>
                        <p className="text-2xl font-bold text-green-900 mt-1">
                          ${selectedCohort.totalRevenue.toFixed(2)}
                        </p>
                      </div>
                      <CurrencyDollarIcon className="h-10 w-10 text-green-500" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Avg Revenue/Patron</p>
                        <p className="text-2xl font-bold text-blue-900 mt-1">
                          ${selectedCohort.patronCount > 0 ? (selectedCohort.totalRevenue / selectedCohort.patronCount).toFixed(2) : '0.00'}
                        </p>
                      </div>
                      <UserGroupIcon className="h-10 w-10 text-blue-500" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-purple-600 font-medium">Churn Rate</p>
                        <p className="text-2xl font-bold text-purple-900 mt-1">
                          {(100 - selectedCohort.retentionRate).toFixed(1)}%
                        </p>
                      </div>
                      <ArrowTrendingDownIcon className="h-10 w-10 text-purple-500" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <ChartBarIcon className="mx-auto h-16 w-16 text-gray-400" />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">Select a Cohort</h3>
              <p className="mt-2 text-sm text-gray-500">
                Choose a cohort from the sidebar to view detailed analytics.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Cohort Modal */}
      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Create New Cohort
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      placeholder="e.g., January 2024 Joiners"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      rows={3}
                      value={createForm.description}
                      onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                      placeholder="Describe the cohort criteria"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Join Date Start</label>
                      <input
                        type="date"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={createForm.joinDateStart}
                        onChange={(e) => setCreateForm({ ...createForm, joinDateStart: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Join Date End</label>
                      <input
                        type="date"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={createForm.joinDateEnd}
                        onChange={(e) => setCreateForm({ ...createForm, joinDateEnd: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Min Pledge Amount ($)</label>
                      <input
                        type="number"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={createForm.minPledgeAmount}
                        onChange={(e) => setCreateForm({ ...createForm, minPledgeAmount: e.target.value })}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Max Pledge Amount ($)</label>
                      <input
                        type="number"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={createForm.maxPledgeAmount}
                        onChange={(e) => setCreateForm({ ...createForm, maxPledgeAmount: e.target.value })}
                        placeholder="Unlimited"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                  onClick={handleCreateCohort}
                >
                  Create
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comparison Modal */}
      {showComparisonModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Cohort Comparison
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cohort</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patrons</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retention</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Churn</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg LTV</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {comparisonData.map((item, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.cohortName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.metrics.patronCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                            {item.metrics.retentionRate.toFixed(1)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                            {item.metrics.churnRate.toFixed(1)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${item.metrics.averageLifetimeValue.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            ${item.metrics.totalRevenue.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="mt-5 sm:mt-6">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  onClick={() => setShowComparisonModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCohortAnalyticsPage;
